<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { facebookSyncSettingsAPI } from 'dashboard/api/facebookImports';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const showAlert = useAlert();

const settings = ref({
  auto_sync_enabled: false,
  sync_frequency: 'manual',
  last_synced_at: null,
});
const isSaving = ref(false);
const isSyncing = ref(false);

const FREQUENCIES = ['manual', 'hourly', 'daily'];

async function loadSettings() {
  try {
    const { data } = await facebookSyncSettingsAPI.get(props.inboxId);
    settings.value = data;
  } catch {
    // new record — defaults used
  }
}

async function save() {
  isSaving.value = true;
  try {
    await facebookSyncSettingsAPI.save(props.inboxId, settings.value);
    showAlert(t('FACEBOOK_IMPORT.ALERTS.SYNC_SETTINGS_SAVED'));
  } finally {
    isSaving.value = false;
  }
}

async function syncNow() {
  isSyncing.value = true;
  try {
    await facebookSyncSettingsAPI.syncNow(props.inboxId);
    showAlert(t('FACEBOOK_IMPORT.ALERTS.SYNC_STARTED'));
  } finally {
    isSyncing.value = false;
  }
}

onMounted(loadSettings);
</script>

<template>
  <div class="space-y-4">
    <h3 class="text-sm font-semibold text-n-slate-12">
      {{ t('FACEBOOK_IMPORT.SYNC.TITLE') }}
    </h3>

    <label class="flex cursor-pointer items-center gap-2">
      <input
        v-model="settings.auto_sync_enabled"
        type="checkbox"
        class="rounded"
      />
      <span class="text-sm text-n-slate-12">
        {{ t('FACEBOOK_IMPORT.SYNC.AUTO_SYNC_LABEL') }}
      </span>
    </label>

    <div>
      <p class="mb-1 text-xs text-n-slate-11">
        {{ t('FACEBOOK_IMPORT.SYNC.FREQUENCY_LABEL') }}
      </p>
      <div class="flex gap-2">
        <label
          v-for="freq in FREQUENCIES"
          :key="freq"
          class="flex cursor-pointer items-center gap-1 text-sm"
        >
          <input v-model="settings.sync_frequency" type="radio" :value="freq" />
          {{ t(`FACEBOOK_IMPORT.SYNC.FREQUENCY.${freq}`) }}
        </label>
      </div>
    </div>

    <p class="text-xs text-n-slate-11">
      {{
        settings.last_synced_at
          ? t('FACEBOOK_IMPORT.SYNC.LAST_SYNCED_VALUE', {
              time: new Date(settings.last_synced_at).toLocaleString(),
            })
          : t('FACEBOOK_IMPORT.SYNC.LAST_SYNCED') +
            ': ' +
            t('FACEBOOK_IMPORT.SYNC.NEVER')
      }}
    </p>

    <div class="flex gap-2">
      <button
        class="rounded bg-woot-500 px-3 py-1.5 text-xs text-white hover:bg-woot-600 disabled:opacity-50"
        :disabled="isSaving"
        @click="save"
      >
        {{ t('FACEBOOK_IMPORT.SYNC.SAVE') }}
      </button>
      <button
        class="rounded border border-n-weak px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-slate-3 disabled:opacity-50"
        :disabled="isSyncing"
        @click="syncNow"
      >
        {{ t('FACEBOOK_IMPORT.SYNC.SYNC_NOW') }}
      </button>
    </div>
  </div>
</template>
